import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light');

  useEffect(() => {
    // Get user's local time (0-23 hours)
    const hour = new Date().getHours();
    
    // Auto-select theme based on time
    // 6 AM (6) to 6 PM (18) = light mode
    // 6 PM (18) to 6 AM (6) = dark mode
    const autoTheme: Theme = (hour >= 6 && hour < 18) ? 'light' : 'dark';
    
    // Check if user has manual preference
    const savedTheme = localStorage.getItem('nexqr-theme') as Theme | null;
    const finalTheme = savedTheme || autoTheme;
    
    setTheme(finalTheme);
    
    // Apply theme to document
    if (finalTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const updateTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    localStorage.setItem('nexqr-theme', newTheme);
    
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme: updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
